import classNames from 'classnames';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { COPY } from '../../assets/copy/copy';
import { FALLBACK_MISSION_IMAGE_URL } from '../../assets/FALLBACK_MISSION_IMAGE_URL.js';
import { LockIcon, StarIcon } from '../../assets/svgs';
import ROUTES from '../../routes/routes.js';
import type {
  MissionLockedReason,
  Reward,
} from '../../store/queries/fragments/mission/missionSubFragments.js';
import { MISSION_ID_PARAM } from '../../utils/routingHelpers.js';
import { ActionTag } from '../ActionTag/ActionTag';
import { LikeButton } from '../LikeButton.js';
import { LockedStatus } from '../LockedStatuses/LockedStatuses';
import { StatusIcons } from '../StatusIcons/StatusIcons';
import styles from './missionCard.module.scss';
import { LimitedInformation, getLimitedClasses } from './missionCardUtils';

export function MissionCard({
  expiringDate,
  expiringSoon,
  featured,
  iLike,
  id,
  lockedStatus,
  name,
  repeats,
  rewards,
  setLike,
  thumbUrl,
  totalLikes,
}: {
  expiringDate: Date | null;
  expiringSoon: boolean;
  featured?: null | boolean;
  iLike?: boolean;
  id: string;
  inProgress: boolean;
  lockedStatus: MissionLockedReason | null | undefined;
  name?: string | null;
  repeats: boolean;
  rewards?: null | readonly Reward[];
  setLike: (id: string, like: boolean) => Promise<void>;
  thumbUrl?: string | null;
  totalLikes?: number;
}): JSX.Element {
  const filteredRewards = rewards?.filter(
    (reward) =>
      reward.__typename === 'Point' || reward.__typename === 'Sweepstake'
  );

  const onChangeMissionLike = useCallback(
    (value: boolean) => setLike(id, value),
    [id, setLike]
  );

  return (
    <Link
      className={classNames(styles['mission-card'], 'shadowed-paper', {
        [styles['mission-locked']]: lockedStatus,
      })}
      to={`${ROUTES.HOME}?${new URLSearchParams({
        [MISSION_ID_PARAM]: id,
      })}`}
    >
      {lockedStatus ? (
        <div className="mission-card-tag">
          <ActionTag icon={<LockIcon />}>{COPY.MISSIONS.LOCKED}</ActionTag>
        </div>
      ) : featured ? (
        <div className="mission-card-tag">
          <ActionTag icon={<StarIcon />}>{COPY.MISSIONS.FEATURED}</ActionTag>
        </div>
      ) : null}

      <img
        className={styles['mission-card-image']}
        src={thumbUrl || FALLBACK_MISSION_IMAGE_URL}
        alt=""
      />

      <h4 className={`mission-card-name`}>{name}</h4>
      <div className={classNames('rewards-list', { gray: lockedStatus })}>
        {filteredRewards?.map((reward) => {
          return (
            <div
              className={classNames(
                'rewards-list-item',
                getLimitedClasses(reward) || 'purple'
              )}
              key={`${reward.__typename}:${reward.category}`}
            >
              <div className={classNames('points', 'bold')}>
                {reward.count} {reward.category}{' '}
              </div>
              <div className="rewards-list-item-sublabel">
                <LimitedInformation reward={reward} />
              </div>
            </div>
          );
        })}
      </div>
      {lockedStatus && (
        <div className="small-text">
          <LockedStatus reasons={lockedStatus} />
        </div>
      )}

      <div className="gray small-text">
        <StatusIcons
          expiringSoon={expiringSoon}
          expiringDate={expiringDate}
          repeats={repeats ?? false}
        />
      </div>

      <LikeButton
        iLike={iLike ?? false}
        onChange={onChangeMissionLike}
        totalLikes={totalLikes ?? 0}
      />
    </Link>
  );
}
